import _defineProperty from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["jsx", "format", "outputFormat", "providerImportSource", "recmaPlugins", "rehypePlugins", "remarkPlugins", "SourceMapGenerator"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/**
 * @typedef {import('unified').Processor} Processor
 * @typedef {import('unified').PluggableList} PluggableList
 * @typedef {import('./plugin/recma-document.js').RecmaDocumentOptions} RecmaDocumentOptions
 * @typedef {import('./plugin/recma-stringify.js').RecmaStringifyOptions} RecmaStringifyOptions
 * @typedef {import('./plugin/recma-jsx-rewrite.js').RecmaJsxRewriteOptions} RecmaJsxRewriteOptions
 *
 * @typedef BaseProcessorOptions
 * @property {boolean} [jsx=false] Whether to keep JSX
 * @property {'mdx'|'md'} [format='mdx'] Format of the files to be processed
 * @property {'program'|'function-body'} [outputFormat='program'] Whether to compile to a whole program or a function body.
 * @property {string[]} [mdExtensions] Extensions (with `.`) for markdown
 * @property {string[]} [mdxExtensions] Extensions (with `.`) for MDX
 * @property {PluggableList} [recmaPlugins] List of recma (esast, JavaScript) plugins
 * @property {PluggableList} [remarkPlugins] List of remark (mdast, markdown) plugins
 * @property {PluggableList} [rehypePlugins] List of rehype (hast, HTML) plugins
 *
 * @typedef {Omit<RecmaDocumentOptions & RecmaStringifyOptions & RecmaJsxRewriteOptions, 'outputFormat'>} PluginOptions
 * @typedef {BaseProcessorOptions & PluginOptions} ProcessorOptions
 */
import unified from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { recmaJsxBuild } from './plugin/recma-jsx-build.js';
import { recmaDocument } from './plugin/recma-document.js';
import { recmaJsxRewrite } from './plugin/recma-jsx-rewrite.js';
import { recmaStringify } from './plugin/recma-stringify.js';
import { rehypeRecma } from './plugin/rehype-recma.js';
import { rehypeRemoveRaw } from './plugin/rehype-remove-raw.js';
import { remarkMarkAndUnravel } from './plugin/remark-mark-and-unravel.js';
import { remarkMdx } from './plugin/remark-mdx.js';
import { nodeTypes } from './node-types.js';
/**
 * Pipeline to:
 *
 * 1. Parse MDX (serialized markdown with embedded JSX, ESM, and  expressions)
 * 2. Transform through remark (mdast), rehype (hast), and recma (esast)
 * 3. Serialize as JavaScript
 *
 * @param {ProcessorOptions} [options]
 * @return {Processor}
 */

export function createProcessor() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var jsx = options.jsx,
      format = options.format,
      outputFormat = options.outputFormat,
      providerImportSource = options.providerImportSource,
      recmaPlugins = options.recmaPlugins,
      rehypePlugins = options.rehypePlugins,
      remarkPlugins = options.remarkPlugins,
      SourceMapGenerator = options.SourceMapGenerator,
      rest = _objectWithoutProperties(options, _excluded); // @ts-ignore runtime.


  if (format === 'detect') {
    throw new Error("Incorrect `format: 'detect'`: `createProcessor` can support either `md` or `mdx`; it does not support detecting the format");
  }

  return unified().use(remarkParse).use(format === 'md' ? undefined : remarkMdx).use(remarkMarkAndUnravel).use(remarkPlugins).use(remarkRehype, {
    allowDangerousHtml: true,
    passThrough: nodeTypes
  }).use(rehypePlugins).use(format === 'md' ? rehypeRemoveRaw : undefined) // @ts-ignore this switches from rehype to recma
  .use(rehypeRecma).use(recmaDocument, _objectSpread(_objectSpread({}, rest), {}, {
    outputFormat: outputFormat
  })).use(recmaJsxRewrite, {
    providerImportSource: providerImportSource,
    outputFormat: outputFormat
  }).use(jsx ? undefined : recmaJsxBuild, {
    outputFormat: outputFormat
  }) // @ts-ignore recma compiler is seen as a transformer
  .use(recmaStringify, {
    SourceMapGenerator: SourceMapGenerator
  }).use(recmaPlugins);
}